import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { RouterOutlet, ActivatedRoute } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Subject, takeUntil } from 'rxjs';
import { environment } from '@environments/environment';
import { AuthService } from '@app/core/auth/auth';
import { SessionStorageService } from '@app/core/auth/session-storage';
import {
  TopBarComponent,
  TopBarItem,
  TopBarTitle,
} from '@app/shared/components/top-bar/top-bar';
import { SkeletonComponent } from '@app/shared/components/skeleton/skeleton';
import { FacadeResolveResult } from '@app/shared/resolvers/facade-resolver';
import { removeHashQuery } from '@app/shared/utils/remove-hash-query/remove-hash-query';
import { WindowRef } from '@app/core/providers/window-ref';

@Component({
  selector: 'app-hospital-layout',
  standalone: true,
  imports: [RouterOutlet, TopBarComponent, SkeletonComponent],
  templateUrl: './hospital-layout.html',
  styleUrls: ['./hospital-layout.scss'],
})
export class HospitalLayoutComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private isMobileSignal = signal(false);
  facadeResultSignal = signal<FacadeResolveResult | null>(null);

  topBarItems: TopBarItem[] = [
    { name: 'account', text: ' / ' },
    { name: 'logout', text: 'ログアウト', action: () => this.onLogout() },
  ];
  topBarTitle: TopBarTitle = { text: '匿名化データレビュー画面' };

  constructor(
    private readonly sessionService: SessionStorageService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly route: ActivatedRoute,
    private readonly authService: AuthService,
    private readonly windowRef: WindowRef,
  ) {}

  ngOnInit(): void {
    // パス付きでログインした場合、urlに認証情報が残ってしまうので除去
    removeHashQuery();

    this.breakpointObserver
      .observe(['(max-width: 1023px)'])
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => this.isMobileSignal.set(result.matches));

    const userId = this.sessionService.getUserId();
    let userName = this.sessionService.getUserName();

    if (!userName) {
      const data = this.route.snapshot.data as {
        facadeResolver?: FacadeResolveResult;
      };
      const res = data.facadeResolver;
      if (res) {
        this.facadeResultSignal.set(res);

        if (res.status === 'success') {
          userName =
            res.practitioner.name.find((n) =>
              n.extension.some((e) => e.valueCode === 'IDE'),
            )?.text ??
            (res.practitioner.name.find((n) =>
              n.extension.some((e) => e.valueCode === 'SYL'),
            )?.text as string);
          this.sessionService.saveUserName(userName);
        }
      }
    }
    this.topBarItems = [
      { name: 'account', text: `${userName ?? ''} / ${userId}` },
      { name: 'logout', text: 'ログアウト', action: () => this.onLogout() },
    ];
    if (this.authService.isAdminUser()) {
      this.topBarTitle = {
        text: '匿名化データレビュー画面',
        url: '/admin/reviews',
      };
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isMobile(): boolean {
    return this.isMobileSignal();
  }

  isFacadeError(): boolean {
    return this.facadeResultSignal()?.status === 'error';
  }

  onLogout(): void {
    this.sessionService.logout();
    this.windowRef.href = `${environment.cgwAuthWebUrl}?applicationCode=${environment.applicationName}#redirectUrl=${environment.baseUrl}`;
  }
}
